import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject, tap } from 'rxjs';

import { AuditCodes, AuditLevels } from '@app/data/dtos/audit/audit.dto';
import { ContactRequestDto } from '@app/data/dtos/contact/contact.dto';
import { UserRepository, USER_REPOSITORY } from '@app/domain/repositories/user/user.repository.contract';
import { PTRAB_SECTIONS_PERMISSIONS } from '@app/entities/constants/ptrab/ptrab-permissions.const';
import { Permissions } from '@app/entities/enums/permissions/permissions.enum';
import { TourKeys } from '@app/entities/enums/tour.enum';
import { NewsItem } from '@app/entities/interfaces/news/news.interface';
import { AppError, ErrorTypes } from '@app/entities/models/error/error.model';
import { Message } from '@app/entities/models/message/message.model';
import { Plate } from '@app/entities/models/personal-data/plates.model';
import { Conditions } from '@app/entities/models/user/conditions.model';
import { UserLogin, UserLoginPhone } from '@app/entities/models/user/login.model';
import { UserOnBoarding } from '@app/entities/models/user/on-boarding.model';
import { RepresentedUser } from '@app/entities/models/user/represented-user.model';
import { User } from '@app/entities/models/user/user.model';
import { ErrorService, Logger } from '@app/services';
import { TokenHelperService } from '@app/services/auth/token.helper.service';
import { StorageService } from '@app/services/storage';
import { FileFetchHelper } from '@app/shared/helpers/file-fetch/file-fetch.helper';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { flatten } from 'lodash-es';

import { MTranslateService } from '@mercadona/core/translate';

import { UserUseCases } from './user.use-cases.contract';
import { AUDIT_USE_CASES, AuditUseCases } from '../audit/audit.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class UserUseCasesImpl implements UserUseCases {
  #userRepository: UserRepository = inject(USER_REPOSITORY);
  #auditUseCases: AuditUseCases = inject(AUDIT_USE_CASES);

  readonly logger = new Logger('UserService');
  // TODO: make private again after adapt tests that are accesing to this property
  userInfo: User | undefined;
  userInfoSubject = new ReplaySubject<User>(1);
  userInfoChanges$: Observable<User> = this.userInfoSubject.asObservable();

  constructor(
    private tokenHelperService: TokenHelperService,
    private storageService: StorageService,
    private errorService: ErrorService,
    private translateService: MTranslateService
  ) {}

  clear(): void {
    this.userInfo = undefined;
    this.userInfoSubject.next(this.userInfo as MSafeAny);
  }

  async getPermissions(): Promise<Permissions[]> {
    const storedUser = await this.getStoredUser();
    return storedUser && this.userInfo ? this.userInfo.permissions : [];
  }

  async getLegalConditionsType(): Promise<string | null> {
    const storedUser = await this.getStoredUser();
    return storedUser && this.userInfo && this.userInfo.legalConditionsType ? this.userInfo.legalConditionsType : null;
  }

  async getStoredUser(): Promise<User> {
    if (!this.userInfo) {
      return this.storageService
        .getUserData()
        .then((user: User) => {
          this.userInfo = user;
          this.userInfoSubject.next(this.userInfo);
          return Promise.resolve(this.userInfo);
        })
        .catch(() => Promise.reject(undefined));
    }
    return Promise.resolve(this.userInfo);
  }

  hasPermissionDownloadPdf(): boolean {
    return Boolean(this.userInfo && this.userInfo.permissions.includes(Permissions.MOT_VACATIONS_PDF));
  }

  hasAccessToPtrabSections(): boolean {
    if (ENV.fullAccessPtrab) {
      return true;
    }

    const ptrabPermissions = flatten(Object.values(PTRAB_SECTIONS_PERMISSIONS));
    const hasSomePermissions = this.userInfo?.permissions.some((permission) => ptrabPermissions.includes(permission));

    return Boolean(hasSomePermissions);
  }

  async requestUserInfo(): Promise<User> {
    const userInfo = await this.#userRepository.requestUserInfo();
    userInfo.photo = await this.preloadUserPhoto(userInfo.photo);
    this.userInfo = userInfo;
    return this.storeUser(true);
  }

  async syncImage(): Promise<string> {
    const response = await this.#userRepository.syncImage();

    if (this.userInfo) {
      this.userInfo.photo = await this.preloadUserPhoto(response.photo);
      this.storeUser();

      return Promise.resolve(this.userInfo.photo);
    }
    return Promise.resolve('');
  }

  async checkData(): Promise<void> {
    const storageUser = await this.storageService.getUserData();

    if (!storageUser || storageUser.userid !== this.userInfo?.userid) {
      this.userInfo = storageUser;
      return this.errorService.add(new AppError(ErrorTypes.CLOSE_APP));
    }

    return Promise.resolve();
  }

  getRepresentedUsers(): Observable<RepresentedUser[]> {
    return this.#userRepository.getRepresentedUsers();
  }

  getContactMessages(): Observable<Message[]> {
    return this.#userRepository.getContactMessages();
  }

  setAcceptConditions(email: string, acceptation: boolean): Observable<object> {
    return this.#userRepository.setAcceptConditions(email, acceptation).pipe(
      tap(() => {
        if (this.userInfo) {
          this.userInfo.acceptLegal = acceptation;
        }
        this.storeUser();
      })
    );
  }

  editAvatar(newAvatar: File): Observable<object> {
    return this.#userRepository.editAvatar(newAvatar);
  }

  deleteAvatar(): Observable<object> {
    return this.#userRepository.deleteAvatar();
  }

  storeAvatar(b64Image: string): Promise<User> {
    this.userInfo = this.userInfo ?? ({} as User);
    this.userInfo.photo = b64Image;
    return this.storeUser();
  }

  setLanguage(language_code: string, language_country: string): Promise<User> {
    return this.#userRepository
      .setLanguage(language_code, language_country)
      .then(() => {
        this.translateService.setActiveLang(language_code);
        return this.storeLanguage(language_code, language_country);
      })
      .catch((error) => {
        const message = `Error trying to change user language code: ${language_code}, country: ${language_country}`;
        this.logger.error(message);
        this.logger.error(error);
        return Promise.reject(message);
      });
  }

  storeLanguage(language_code: string, language_country?: string): Promise<User> {
    this.userInfo = this.userInfo ?? ({} as User);
    this.userInfo.language_code = language_code;
    this.userInfo.language_country = language_country ?? '';
    return this.storeUser();
  }

  getLanguageCode(): string {
    this.userInfo = this.userInfo ?? ({} as User);
    return this.userInfo.language_code;
  }

  postContact(data: ContactRequestDto): Observable<object> {
    return this.#userRepository.postContact(data);
  }

  setAcceptNews(newsItem: NewsItem): Observable<object> {
    return this.#userRepository.setAcceptNews(newsItem).pipe(
      tap(() => {
        const acceptedNew = this.userInfo?.news?.find((item) => item.type === newsItem.type);

        if (acceptedNew) {
          acceptedNew.accepted = true;
        }
        this.storeUser();
      })
    );
  }

  setWalkthroughSeen(walkthrough: TourKeys): Observable<object> {
    return this.#userRepository.setWalkthroughSeen(walkthrough).pipe(
      tap(() => {
        if (this.userInfo) {
          this.userInfo.walkthroughs = this.userInfo.walkthroughs.filter((w) => w !== walkthrough);
        }

        this.storeUser();
      })
    );
  }

  mergeUser(user: User | Conditions) {
    this.userInfo = {
      ...this.userInfo,
      ...(user as User),
      isDiscontinousFixed: user.permissions?.includes(Permissions.DISCOUNTINOUS_FIXED)
    };
  }

  private async preloadUserPhoto(photo: string) {
    const formatTypes = ['data:application/octet-stream;', 'data:text/plain;', 'data:;'];
    if (photo) {
      try {
        const b64Photo: string = await FileFetchHelper.getB64FromUrl(photo);
        const format = formatTypes.find((type) => b64Photo.indexOf(type) > -1);
        return b64Photo.replace(format ?? '', 'data:image/jpeg;');
      } catch {
        this.logger.error('Cannot retrieve user photo');
      }
    }
    return '';
  }

  async storeUser(audit?: boolean): Promise<User> {
    const hasAdfsSession = await this.tokenHelperService.hasSession();
    if (!hasAdfsSession) {
      return Promise.reject('User not logged');
    }

    await this.storageService.storeUserData(this.userInfo as User);

    this.userInfoSubject.next(this.userInfo as User);
    if (audit) {
      await this.#auditUseCases.push({
        level: AuditLevels.INFO,
        message: `storeUser success, userInfo is: ${this.userInfo?.name}, ${this.userInfo?.userid}`,
        status_code: AuditCodes.SUCCESS,
        app_component: 'UserService'
      });
    }

    return Promise.resolve(this.userInfo as User);
  }

  getOnBoarding(): Observable<UserOnBoarding> {
    return this.#userRepository.getOnBoarding();
  }

  updateOnBoarding(): Observable<object> {
    return this.#userRepository.updateOnBoarding();
  }

  getUserLogin(userId: string): Observable<UserLogin> {
    return this.#userRepository.getUserLogin(userId);
  }

  checkUserLoginPhone(userId: string, phone: string): Observable<UserLoginPhone> {
    return this.#userRepository.checkUserLoginPhone(userId, phone);
  }

  getLicensePlates(): Observable<Plate[]> {
    return this.#userRepository.getLicensePlates();
  }

  postLicensePlates(data: Plate[]): Observable<void> {
    return this.#userRepository.postLicensePlates(data);
  }

  deleteLicensePlate(licensePlate: string): Observable<void> {
    return this.#userRepository.deleteLicensePlate(licensePlate);
  }
}
